import { T, FEEDS } from './constants';

// All decision/trade timestamps are rendered in US Eastern time so the
// dashboard matches the TradingView strategy report the user reconciles
// against. Using IANA "America/New_York" — DST is handled by the runtime,
// no manual EST/EDT toggle required.
const DISPLAY_TZ = 'America/New_York';

export const fmtTime = ts => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
      timeZone: DISPLAY_TZ,
    });
  } catch { return ts; }
};

export const fmtDateTime = ts => {
  if (!ts) return '—';
  try {
    // sv-SE locale → "YYYY-MM-DD HH:MM:SS" formatting, ET by IANA tz
    return new Date(ts).toLocaleString('sv-SE', {
      hour12: false,
      timeZone: DISPLAY_TZ,
    });
  } catch { return ts; }
};

export const fmtDate = ts => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric',
      timeZone: DISPLAY_TZ,
    });
  } catch { return ts; }
};

export const fmtPrice = n => {
  const v = parseFloat(n);
  if (!v) return '—';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export const mlColor = s => {
  const n = parseFloat(s) || 0;
  if (n >= 80) return T.bull;
  if (n >= 60) return T.gold;
  return T.bear;
};

export const gradeColor = g => {
  // GBEMI/PADA v2 grade overlay tiers — emerald/blue/amber/red.
  if (g === 'A_PREMIUM')   return { color: '#10b981', bg: 'rgba(16,185,129,0.10)',  border: 'rgba(16,185,129,0.4)' };
  if (g === 'B_strong')    return { color: '#3b82f6', bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.4)' };
  if (g === 'C_caution')   return { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.4)' };
  if (g === 'D_high_risk') return { color: '#ef4444', bg: 'rgba(239,68,68,0.10)',   border: 'rgba(239,68,68,0.4)' };
  // STRATA grades (tier_v1) per Mansa Portfolio v1.10 Combined Book.
  // Mirror the A/B/C/SKIP colour cadence of GBEMI's quality grades.
  if (g === 'TREND_A')     return { color: '#10b981', bg: 'rgba(16,185,129,0.10)',  border: 'rgba(16,185,129,0.4)' };
  if (g === 'TREND_B')     return { color: '#3b82f6', bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.4)' };
  if (g === 'TREND_C')     return { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.4)' };
  if (g === 'TREND_SKIP')  return { color: T.textMuted, bg: 'rgba(79,89,102,0.1)', border: 'rgba(79,89,102,0.3)' };
  // Legacy single-letter / status grades.
  if (g === 'A')       return { color: T.bull,    bg: T.bullBg,    border: T.bullBorder };
  if (g === 'B')       return { color: T.gold,    bg: 'rgba(240,185,11,0.08)', border: 'rgba(240,185,11,0.3)' };
  if (g === 'C')       return { color: '#f0b90b', bg: 'rgba(240,185,11,0.05)', border: 'rgba(240,185,11,0.2)' };
  if (g === 'OBSERVE') return { color: T.purple,  bg: T.purpleBg,  border: T.purpleBorder };
  if (g === 'BASE' || g === 'n/a' || g === '?' || g === '-' || g === 'no grade') {
    return { color: T.textMuted, bg: 'rgba(79,89,102,0.1)', border: 'rgba(79,89,102,0.3)' };
  }
  return { color: T.bear, bg: T.bearBg, border: T.bearBorder };
};

export const dirStyle = dir => ({
  background: dir === 'LONG' ? T.bullBg  : T.bearBg,
  color:      dir === 'LONG' ? T.bull    : T.bear,
  border:     `1px solid ${dir === 'LONG' ? T.bullBorder : T.bearBorder}`,
});

export const catColor = cat => {
  if (cat === 'Trend')          return T.blue;
  if (cat === 'Mean Reversion') return T.bull;
  if (cat === 'Hybrid')         return T.gold;
  return T.textDim;
};

// ── Concentrate source-discipline sizing ────────────────────────────────────
// cert NCHE-EXITMOD-SRCDISC-20260617-2CB29373. The advisory size multiplier a
// trader applies BEFORE entry. Mirror of the backend exitmod/labels.py +
// weights.py (backend is canonical; this is the display side):
//   Cat 1 = 1.5×  strong source (GBEMI/TIER1, EMEKA-HL/QS) or NCHE AA_SWING4
//   Cat 2 = 1.0×  neutral (NCHE MW15, below-VWAP longs, or unknown VWAP grade)
//   Cat 3 = 0.5×  above-VWAP longs (FAVOLA with vwap_dist >= 0). GEMINI retired 2026-06-26.
// Returns null for systems outside the Concentrate book (e.g. STRATA stage0).
const _STRONG_SRC = ['GBEMI', 'EMEKA-HL', 'EMEKA-QS'];
export const sizeCategory = d => {
  if (!d) return null;
  const sys = String(d.system_id || d.system || '').toUpperCase();
  let source, grade = '';
  if (sys.startsWith('EMEKA')) {
    source = 'EMEKA-QS';                       // both EMEKA arms are strong (1.5×)
  } else if (sys === 'TIER1' || sys === 'TIER1_APREMIUM' || sys === 'A_PREMIUM' || sys === 'GBEMI') {
    source = 'GBEMI';
  } else if (sys === 'NCHE') {
    source = 'NCHE';
    grade  = String(d.algo || d.grade || '').toUpperCase();
  // FAVOLA removed 2026-06-29 (Option A full wipe).
  } else if (sys === 'OSUPA' || sys === 'WURA') {
    // OṢUPA + WURA are deployed flat 1u Phase 0 / Phase A (sizing OFF in the
    // kit). They sit OUTSIDE the Concentrate sizing scheme but the desk asked
    // for a CAT badge on their toasts too — default to CAT 2 (1.0× neutral)
    // until the kit promotes them to sized phases.
    source = sys === 'OSUPA' ? 'OSUPA' : 'WURA';
  } else {
    return null;                               // not in the Concentrate book (IMPERIAL etc.)
  }
  let weight;
  if (_STRONG_SRC.includes(source) || grade === 'AA_SWING4') weight = 1.5;
  else if (grade === 'ABOVE_VWAP')                           weight = 0.5;
  else                                                       weight = 1.0;
  const cat   = weight === 1.5 ? 1 : (weight === 1.0 ? 2 : 3);
  const color = weight === 1.5 ? T.bull : (weight === 0.5 ? T.gold : T.textDim);
  return {
    cat, weight, color,
    label:    `${weight.toFixed(1)}×`,        // weight only (tooltip / legend)
    catLabel: `CAT ${cat}`,                   // desk-facing badge text
    tip:      `CAT ${cat} · ${weight.toFixed(1)}× size · source ${source}${grade ? ' / ' + grade : ''}`,
  };
};

// NQ-PB-M / NQ-PB-A / CAV1 / An'De / ABAMETTA / Imperial / P1-S1 Levels
// emit their full Pine system names as system_id; treat them as-is.
const NQPBM_SYSTEM_ID       = 'NQ_LONG_PATH_B_MORN_V12C';
const NQPBA_SYSTEM_ID       = 'NQ_LONG_PATH_B_AFT_V13';
const CAV1_SYSTEM_ID        = 'CONFIG_A_V1';
// ANDE combined ORB retired 2026-05-17.
// ANDE_LONGS / ANDE_SHORTS removed 2026-06-15 (full wipe).
const ABM_SYSTEM_ID         = 'ABAMETTA';
const IMPERIAL_SYSTEM_ID    = 'IMPERIAL_v3_1';
// P1S1_LEVELS_SYSTEM_ID retired 2026-06-11 (full P1-S1 wipe).
const ASIKO_IGBEGA_V2_SYSTEM_ID  = 'ASIKO_IGBEGA_V2';
const MANSA_AA4L_NLA_SYSTEM_ID   = 'MANSA_AA4_LONGS_NLA';
const MANSA_AA4S_NLA_SYSTEM_ID   = 'MANSA_AA4_SHORTS_NLA';
const DIMANCHE_V2_SYSTEM_ID      = 'DIMANCHE_V2';
const DOMINGO_SHORTS_SYSTEM_ID   = 'DOMINGO_SHORTS';
const DOMINGO_LONGS_SYSTEM_ID    = 'DOMINGO_LONGS';
// GBEMI_SYSTEM_ID retired 2026-06-10 (full GBEMI wipe).
const PADA_SYSTEM_ID             = 'PADA';
const EXIT_ROUTER_SYSTEM_ID      = 'EXIT_ROUTER';

export const systemColor = sysId => {
  if (sysId === 'S0-A')  return '#9c6ef5';   // purple-A
  if (sysId === 'S0-B')  return '#c084fc';   // purple-B (lighter)
  if (sysId === NQPBM_SYSTEM_ID)        return FEEDS.NQPBM.color;
  if (sysId === NQPBA_SYSTEM_ID)        return FEEDS.NQPBA.color;
  if (sysId === CAV1_SYSTEM_ID)         return FEEDS.CAV1.color;
  if (sysId === ABM_SYSTEM_ID)          return FEEDS.ABM.color;
  if (sysId === IMPERIAL_SYSTEM_ID)     return FEEDS.IMPERIAL.color;
  if (sysId === ASIKO_IGBEGA_V2_SYSTEM_ID)   return FEEDS.ASIKO_IGBEGA_V2.color;
  if (sysId === MANSA_AA4L_NLA_SYSTEM_ID)    return FEEDS.MANSA_AA4_LONGS_NLA.color;
  if (sysId === MANSA_AA4S_NLA_SYSTEM_ID)    return FEEDS.MANSA_AA4_SHORTS_NLA.color;
  if (sysId === DIMANCHE_V2_SYSTEM_ID)        return FEEDS.DIMANCHE_V2.color;
  if (sysId === DOMINGO_SHORTS_SYSTEM_ID)     return FEEDS.DOMINGO_SHORTS.color;
  if (sysId === DOMINGO_LONGS_SYSTEM_ID)      return FEEDS.DOMINGO_LONGS.color;
  if (sysId === PADA_SYSTEM_ID)               return FEEDS.PADA.color;
  if (sysId === EXIT_ROUTER_SYSTEM_ID)        return FEEDS.EXIT_ROUTER.color;
  return T.textDim;
};

// Detect if a system_id belongs to Stage 0
export const isStage0 = sysId => sysId?.startsWith('S0-');

// Display-name mapping for system ids.
// The backend keeps emitting S0-A / S0-B; only the UI renames them so routes,
// dedupe keys, feed merging, and systemColor/isStage0 lookups stay intact.
const SYSTEM_DISPLAY_NAMES = {
  'S0-A': 'MV-ETH-20',
  'S0-B': 'MV-ETH-35',
  [NQPBM_SYSTEM_ID]:  'NQ-PB-M',
  [NQPBA_SYSTEM_ID]:  'NQ-PB-A',
  [CAV1_SYSTEM_ID]:   'CAV1',
  [ABM_SYSTEM_ID]:         'ABAMETTA',
  [IMPERIAL_SYSTEM_ID]:    'Imperial',
  [ASIKO_IGBEGA_V2_SYSTEM_ID]: 'Asiko Igbega v2',
  [MANSA_AA4L_NLA_SYSTEM_ID]:  'Mansa AA4 Longs',
  [MANSA_AA4S_NLA_SYSTEM_ID]:  'Mansa AA4 Shorts',
  [DIMANCHE_V2_SYSTEM_ID]:     'Dimanche v2',
  [DOMINGO_SHORTS_SYSTEM_ID]:  'Domingo Shorts',
  [DOMINGO_LONGS_SYSTEM_ID]:   'Domingo Longs',
  [PADA_SYSTEM_ID]:            'Padà',
  [EXIT_ROUTER_SYSTEM_ID]:     'Exit Router',
};

export const systemLabel = sysId => SYSTEM_DISPLAY_NAMES[sysId] || sysId;

// Build the /decisions/:system/:key URL for a dashboard row.
// Only Stage 0 (S0-*) and CAV1 currently have working detail endpoints. Every
// other system returns null so the row stays non-clickable rather than 404ing.
// Backend always supplies detail_key for the supported systems.
export const detailHref = d => {
  if (!d?.system_id) return null;
  const isStage0Sys = d.system_id.startsWith('S0-');
  const isCav1Sys   = d.system_id === CAV1_SYSTEM_ID;
  if (!isStage0Sys && !isCav1Sys) return null;
  const key = d.detail_key ?? d.id;
  if (key == null) return null;
  return `/decisions/${encodeURIComponent(d.system_id)}/${encodeURIComponent(key)}`;
};

// Compute realized (or live) P/L for a decision row when an exit is known.
// Returns { pts, usd, status, exitReason } or null if we can't compute.
// - Checks d.trade.* first (nested shape from detail endpoint), then flat fields
// - USD is only shown if the backend provided it; we don't guess the instrument
//   multiplier on the frontend (NQ is $20/pt, MNQ $2/pt, ES $50/pt, etc.)
export const computePnL = d => {
  if (!d) return null;
  const trade   = d.trade;
  const exitPx  = trade?.exit_px  ?? d.exit_px  ?? d.exit_price;
  const entryPx = trade?.entry_px ?? d.entry_price ?? d.entry_px;
  const dir     = d.direction;
  if (exitPx == null || entryPx == null || !dir) return null;
  const entry = parseFloat(entryPx);
  const exit  = parseFloat(exitPx);
  if (Number.isNaN(entry) || Number.isNaN(exit)) return null;
  // Prefer the backend's BOOKED pl_pts (includes slippage/commission) so the
  // cross-strategy log matches the per-strategy monitors; fall back to a
  // price-derived estimate only when the backend didn't provide it. (Imperial
  // books -15.75pt with 2pt slippage; raw exit-entry would read -13.75.)
  const ptsRaw = trade?.pl_pts ?? d.pl_pts;
  const pts    = ptsRaw != null ? parseFloat(ptsRaw)
                                : (dir === 'SHORT' ? (entry - exit) : (exit - entry));
  const usdRaw = trade?.pl_usd ?? d.pl_usd;
  return {
    pts:        Math.round(pts * 100) / 100,
    usd:        usdRaw != null ? parseFloat(usdRaw) : null,
    status:     trade?.status ?? d.trade_status ?? 'CLOSED',
    exitReason: trade?.exit_reason ?? d.exit_reason ?? null,
  };
};

export const secAgo = ts => {
  if (!ts) return null;
  return Math.floor((Date.now() - new Date(ts)) / 1000);
};
